import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { InfoDot } from "./labels";
import { useLocationClockConfig, useLocationTrackTemplateMutation, useSlaMutation } from "./hooks";
import type { LocationClockConfigRow } from "./types";

/**
 * Zakładka „Terminy”: zegary uzgodnień lokalizacji per typ właściciela działki —
 * dokładnie lista biznesowa („JST — 5 dni my, 60 dni urząd”…), edytowalna inline
 * (zapis = zwykła reguła SLA applies_to=location), plus meta-ścieżka (szablon toru)
 * per kategoria z resetem istniejących działek.
 */

export const ELEMENT_KIND_LABELS: Record<string, string> = {
  wniosek: "Wniosek do urzędu",
  wpw_teren: "Negocjator (WPW)",
  zgloszenie_ze: "Zgłoszenie do ZE",
  uzupelnienie: "Uzupełnienie",
  odmowa_urzedu: "Odmowa urzędu",
  odwolanie: "Odwołanie",
  ponowny_wniosek: "Ponowny wniosek",
  art124: "Art. 124",
  przeprojektowanie: "Przeprojektowanie",
  inny: "Inny",
};

function ClockRow({ row }: { row: LocationClockConfigRow }) {
  const m = useSlaMutation();
  const [our, setOur] = useState<number>(row.our_days);
  const [their, setTheir] = useState<number>(row.their_days);
  const dirty = our !== row.our_days || their !== row.their_days;

  const save = () => {
    if (row.rule_id) {
      m.update.mutate({ id: row.rule_id, our_sla_days: our, sla_days: their });
    } else {
      m.create.mutate({
        scope: "branch", branch: "energy", applies_to: "location", key: row.key,
        our_sla_days: our, sla_days: their, warn_days: row.warn_days,
        responsible_role: row.counterparty === "negocjator" ? "negotiator" : "coordinator",
        is_active: true,
      });
    }
  };

  return (
    <tr className="border-b border-[var(--color-border)]">
      <td className="py-1.5 pr-2">
        <span className="flex items-center gap-1">
          <b>{row.label}</b>
          {row.help_text && <InfoDot text={row.help_text} />}
          {!row.is_category && <Badge color="gray">element toru</Badge>}
        </span>
        <span className="font-mono text-[9px] text-[var(--color-text-muted)]">{row.key}</span>
      </td>
      <td className="pr-2">
        <input type="number" min={0} className="input-base w-16 !py-0.5 text-xs" value={our}
          onChange={(e) => setOur(Number(e.target.value))} />
      </td>
      <td className="pr-2 text-xs">
        <Badge color={row.counterparty === "negocjator" ? "yellow" : "gray"}>{row.counterparty}</Badge>
      </td>
      <td className="pr-2">
        {row.their_days > 0 || their > 0 ? (
          <input type="number" min={0} className="input-base w-16 !py-0.5 text-xs" value={their}
            onChange={(e) => setTheir(Number(e.target.value))} />
        ) : (
          <span className="text-[10px] font-mono text-[var(--color-text-muted)]">— (tylko nasza faza)</span>
        )}
      </td>
      <td className="pr-2">
        {dirty && (
          <button className="text-xs text-[var(--color-primary)] font-bold"
            disabled={m.create.isPending || m.update.isPending} onClick={save}>
            zapisz
          </button>
        )}
      </td>
    </tr>
  );
}

function TrackTemplateEditor({ row }: { row: LocationClockConfigRow }) {
  const tpl = useLocationTrackTemplateMutation();
  const [kinds, setKinds] = useState<string[]>(row.track_template ?? []);
  const [addKind, setAddKind] = useState("wniosek");
  const dirty = JSON.stringify(kinds) !== JSON.stringify(row.track_template ?? []);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] w-44 shrink-0">
        {row.label}
      </span>
      {kinds.map((k, i) => (
        <span key={`${k}-${i}`}
          className="inline-flex items-center gap-1 text-[10px] border border-[var(--color-border)] rounded px-1.5 py-0.5 bg-[var(--color-bg-elevated)]">
          {i + 1}. {ELEMENT_KIND_LABELS[k] ?? k}
          <button className="text-[var(--color-primary)]"
            onClick={() => setKinds(kinds.filter((_, j) => j !== i))} title="Usuń z szablonu">×</button>
        </span>
      ))}
      <select className="input-base !py-0 text-[10px] w-36" value={addKind}
        onChange={(e) => setAddKind(e.target.value)}>
        {Object.entries(ELEMENT_KIND_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <button className="text-[10px] font-mono border border-dashed border-[var(--color-border)] px-1.5 py-0.5"
        onClick={() => setKinds([...kinds, addKind])}>+ dodaj</button>
      {dirty && (
        <button className="text-xs text-[var(--color-primary)] font-bold"
          disabled={tpl.save.isPending || kinds.length === 0}
          onClick={() => tpl.save.mutate({ category: row.key, kinds })}>
          zapisz szablon
        </button>
      )}
      <button
        className="text-[10px] text-[var(--color-text-muted)] underline"
        disabled={tpl.reset.isPending}
        title="Dosiewa zaplanowane bloczki z aktualnego szablonu na WSZYSTKICH istniejących działkach tej kategorii (zrobione/aktywne/ręczne bez zmian)."
        onClick={() => {
          if (window.confirm(`Zresetować zaplanowane tory wszystkich działek kategorii „${row.label}” do aktualnego szablonu?`)) {
            tpl.reset.mutate(row.key, {
              onSuccess: (r) => window.alert(`Zresetowano tory ${r.parcels_reset} działek.`),
            });
          }
        }}>
        {tpl.reset.isPending ? "resetuję…" : "resetuj istniejące"}
      </button>
    </div>
  );
}

export function LocationClocksCard() {
  const { data: rows } = useLocationClockConfig();
  const categories = (rows ?? []).filter((r) => r.is_category);
  const extras = (rows ?? []).filter((r) => !r.is_category);

  return (
    <div>
      <p className="text-xs text-[var(--color-text-muted)] mb-2">
        Każda działka w trasie ma <b>kategorię właściciela</b> (wyliczaną automatycznie z kodu
        własności EGiB, użytku i właścicieli — z ręcznym nadpisaniem na działce) i zegar
        „<b>my X dni → oni Y dni</b>”. Te zegary są zawartością megakamienia „Uzgodnienie
        lokalizacji”: napędzają prognozę końca projektu, rozwinięcie na grafie i alarmy w
        Kontroli/TODO. Wartości poniżej to defaulty firmowe — wyjątek per klient / projekt /
        działka robisz w formularzu na dole (Czego pilnuje: „Uzgodnienie lokalizacji”).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
              <th className="py-1.5 pr-2">Kategoria działki</th>
              <th className="pr-2">My [dni]<InfoDot text="Nasza faza: ile dni mamy na przygotowanie/złożenie/reakcję, zanim pozycja robi się czerwona." /></th>
              <th className="pr-2">Odpowiada</th>
              <th className="pr-2">Oni [dni]<InfoDot text="Faza drugiej strony: ile dni dajemy urzędowi/negocjatorowi na odpowiedź. Każda runda ping-ponga restartuje zegar strony." /></th>
              <th className="pr-2"></th>
            </tr>
          </thead>
          <tbody>
            {/* klucz z wartościami — po zapisie/refetchu wiersz montuje się od nowa ze świeżym stanem */}
            {categories.map((r) => <ClockRow key={`${r.key}-${r.our_days}-${r.their_days}`} row={r} />)}
          </tbody>
        </table>
      </div>

      <details className="mt-2">
        <summary className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] cursor-pointer">
          Zegary elementów toru ({extras.length}) — eskalacje i kroki specjalne (art. 124, uzupełnienie, zgłoszenie do ZE…)
        </summary>
        <table className="w-full text-sm mt-1">
          <tbody>
            {extras.map((r) => <ClockRow key={`${r.key}-${r.our_days}-${r.their_days}`} row={r} />)}
          </tbody>
        </table>
      </details>

      <details className="mt-2">
        <summary className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] cursor-pointer">
          Meta-ścieżki — domyślny tor („bloczki”) nowej działki per kategoria
        </summary>
        <p className="text-[11px] text-[var(--color-text-muted)] mt-1 mb-2">
          Nowo sklasyfikowana działka dostaje z góry zaplanowaną sekwencję elementów toru —
          prognoza od pierwszego dnia liczy pełną typową ścieżkę. Zmiana szablonu działa na
          PRZYSZŁE działki; istniejące zaktualizujesz przyciskiem „resetuj istniejące”.
        </p>
        <div className="space-y-1.5">
          {categories.map((r) => (
            <TrackTemplateEditor key={`${r.key}-${(r.track_template ?? []).join(",")}`} row={r} />
          ))}
        </div>
      </details>
    </div>
  );
}
